import {
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  useTheme,
} from "@material-ui/core";
import { Form, Formik, FormikProps } from "formik";
import React, { useContext, useEffect } from "react";
import moment, { Moment } from "moment";

import Button from "../Button";
import DatePicker from "../DatePicker";
import { LangContext } from "../../context/LangContext";
import { UserContext } from "../../context/UserContext";
import { accessControl } from "../../constants/enums";
import { hasUserAccess } from "../../utils";
import translations from "../../assets/json/translations.json";

interface IProps {
  onSubmit: (values: IFormValues, initialLoad?: boolean) => void;
  onClear: () => void;
  loadForm: IFormValues;
  formCleared: boolean;
}

export interface IFormValues {
  patientSsn: string;
  requestorSsn: string;
  examinationDateFrom: Moment | null;
  examinationDateTo: Moment | null;
  specimenId: string;
  showPendingHPVResult: boolean;
  showPendingPathologyResult: boolean;
  showAllResultsReceived: boolean;
  showHPVPositiveAndNoPathologyRequest: boolean;
}

type IFormik = FormikProps<IFormValues>;

const SentScreeningRequestsFilter: React.FC<IProps> = ({
  onSubmit,
  onClear,
  loadForm,
  formCleared,
}) => {
  const theme = useTheme();
  const [lang] = useContext(LangContext);
  const [user] = useContext(UserContext);

  useEffect(() => {
    if (loadForm && !formCleared) {
      onSubmit(loadForm, true);
    }
    /* eslint-disable */
  }, [loadForm]);

  return (
    <Formik
      onSubmit={(values) => onSubmit(values)}
      initialValues={getInitalValues()}
      enableReinitialize
    >
      {(formik) => (
        <Form onSubmit={formik.handleSubmit}>{renderFilters(formik)}</Form>
      )}
    </Formik>
  );

  function renderFilters(formik: IFormik) {
    const { values, handleChange, handleBlur, setFieldValue, isSubmitting } =
      formik;
    return (
      <Paper
        elevation={2}
        style={{ padding: theme.spacing(2), marginBottom: theme.spacing(2) }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md>
            <TextField
              fullWidth
              autoFocus={hasUserAccess(accessControl.SENT_SCREENING_REQUESTS_FULL_ACCESS, user)}
              autoComplete="off"
              id="patientSsn"
              label={translations.patientSsn[lang]}
              variant="outlined"
              inputProps={{ minLength: 10, maxLength: 10 }}
              value={values.patientSsn}
              onBlur={handleBlur}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6} md>
            <TextField
              fullWidth
              autoComplete="off"
              id="requestorSsn"
              label={translations.requestorSsn[lang]}
              variant="outlined"
              inputProps={{ maxLength: 10 }}
              value={values.requestorSsn}
              onBlur={handleBlur}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6} md>
            <TextField
              fullWidth
              autoFocus={hasUserAccess(accessControl.SENT_SCREENING_REQUESTS_PATHOLOGISTS, user)}
              autoComplete="off"
              id="specimenId"
              label={translations.specimenId[lang]}
              variant="outlined"
              inputProps={{ minLength: 11, maxLength: 12 }}
              value={values.specimenId}
              onBlur={handleBlur}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} sm={6} md>
            <DatePicker
              id="examinationDateFrom"
              label={translations.examinationDateFrom[lang]}
              value={values.examinationDateFrom}
              muiProps={{
                disableToolbar: true,
                maxDate: values.examinationDateTo || undefined,
              }}
              onChange={(e) =>
                setFieldValue("examinationDateFrom", e ? moment(e).startOf('day') : null)
              }
              onBlur={handleBlur}
            />
          </Grid>

          <Grid item xs={12} sm={6} md>
            <DatePicker
              id="examinationDateTo"
              label={translations.examinationDateTo[lang]}
              value={values.examinationDateTo}
              muiProps={{
                disableToolbar: true,
                minDate: values.examinationDateFrom || undefined,
              }}
              onChange={(e) =>
                setFieldValue("examinationDateTo", e ? moment(e).endOf('day') : null)
              }
              onBlur={handleBlur}
            />
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md>
            <FormControlLabel
              label={translations.HPVResultPending[lang]}
              control={
                <Checkbox
                  id="showPendingHPVResult"
                  color="primary"
                  checked={values.showPendingHPVResult}
                  onChange={(e) =>{
                    setFieldValue("showPendingHPVResult", !values.showPendingHPVResult)
                  }}
                  onBlur={handleBlur}
                />
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md>
            <FormControlLabel
              label={translations.pathologyResultPending[lang]}
              control={
                <Checkbox
                  id="showPendingPathologyResult"
                  color="primary"
                  checked={values.showPendingPathologyResult}
                  onChange={(e) =>{
                    setFieldValue("showPendingPathologyResult", !values.showPendingPathologyResult)
                  }}
                  onBlur={handleBlur}   
                />
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md>
            <FormControlLabel
              label={translations.allResultsReceived[lang]}
              control={
                <Checkbox
                  id="showAllResultsReceived"
                  color="primary"
                  checked={values.showAllResultsReceived}
                  onChange={(e) =>{
                    setFieldValue("showAllResultsReceived", !values.showAllResultsReceived)
                  }}
                  onBlur={handleBlur}
                />
              }
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md>
          <FormControlLabel
              label={translations.HPVPositiveAndNoPathologyRequest[lang]}
              control={
                <Checkbox
                  id="showHPVPositiveAndNoPathologyRequest"
                  color="primary"
                  checked={values.showHPVPositiveAndNoPathologyRequest}
                  onChange={(e) =>{
                    setFieldValue("showHPVPositiveAndNoPathologyRequest", !values.showHPVPositiveAndNoPathologyRequest)
                  }}
                  onBlur={handleBlur}
                />
              }
            />
          </Grid>

          <Grid item sm={12} md>
              <Button style={{width:'49%', marginRight:'2%'}}  type="submit" disabled={isSubmitting}>
                {translations.search[lang]}
              </Button>
              <Button style={{width:'49%'}} variant="outlined"
                onClick={() => {
                  clearForm();
                }}
              >
                {translations.clear[lang]}
              </Button>
          </Grid>
        </Grid>
      </Paper>
    );
  }

  function onFilterChange(summoningFilter: boolean, commentFilter: boolean) {
  }

  function clearForm() {
    onClear();
  }

  function getInitalValues() {
    return loadForm;
  }
};

export default SentScreeningRequestsFilter;
